import * as React from 'react';
import { observer, Provider } from 'mobx-react';
import CSSTransition from 'react-transition-group/CSSTransition';
import { WebGivingMainViewModel } from './webgiving-main-view-model';
import { Router, WebGivingPage } from '../pages';
import { WebGivingMainHeader } from './header/webgiving-main-header';
import { HeroImage } from '../components/hero-image/hero-image';
import { extended as extendedHero } from '../components/hero-image/hero-image.less';
import { WebGivingMainFooter } from './footer/footer';
import { Panel, PanelType } from '../components/panel';
import { BrandingTheme } from '../branding/branding-theme';
import { Backdrop } from '../components/backdrop/backdrop';
import { WebGivingMainBannerExtended } from './banner/webgiving-main-banner';
import { generateManageAccountUrl } from '../utils/identity-utils';
import { NotificationController } from '../components/notification/notification';
import { classNames } from '../../Shared/utils/classnames';
import { ModalCommodore } from '../modals/modal-commodore';
import { getInjectablePropsForProvider } from '../utils/injectable-props';
import { ErrorBoundary } from '../../Shared/components/error-boundary';
import {
	main,
	mainPanel,
	mainPanelLifted,
	wrapper,
	container,
	eclipsed,
	overlay,
	transitionDurationMs,
	transitionDurationLongMs,
	loggedIn,
	loading,
	overlayEnter,
	overlayEnterActive,
	overlayExitActive,
	fadeEnter,
	fadeEnterActive,
	fadeExitActive,
	header,
	mainFooterSpace,
	confettiWrapper,
	pageTitle,
} from './webgiving-main.less';
import 'polyfill-array-includes';
import { ConfettiAnimation } from '../../Shared/components/confetti/confetti';
import { EvidentlyOverridesWidget } from '../evidently';
import { footer as pageGiveFooter } from '../pages/page-give/page-give.less';
import { ShouldTranslateProvider } from '../translation';
import { UiConfigurationContextProvider } from '../providers/uiconfiguration';
import { PaymentMethodType } from '../webgiving-generated';
import { PageTitle } from "../components/page-title/page-title";

@observer
export class WebGivingMain extends React.Component<{ vm: WebGivingMainViewModel }> {
	render() {
		const {
			raiseAction,
			person,
			currentPageViewModel,
			theme,
			scrollStateStore,
			isAccountDetailsOpen,
			webGivingPushpayInfo,
			isAppLoading,
			viewData: { MerchantInfo: merchant, BrandingPackage: brandingPackage },
			sagaContext: { appState },
			hideAnimation,
			animationType,
			notification,
			hasTranslationEnabled,
			unavailableLanguages,
			uiConfiguration,
			setAppLoading,
			failedToLoadLanguage,
			setCurrentPageShouldBeVisible,
			setIsAccountDetailsOpen,
			reportError,
		} = this.props.vm;
		const pageIsGive = currentPageViewModel.type === WebGivingPage.Give;
		const showPayerCoverFeesLegalText =
			currentPageViewModel.type === WebGivingPage.ConfirmGift && merchant.SupportsPayerCoversFees;
		const overlayClassNames = {
			appear: overlayEnter,
			appearActive: overlayEnterActive,
			enter: overlayEnter,
			enterActive: overlayEnterActive,
			exitActive: overlayExitActive,
		};
		const fadeTransitionClassNames = {
			appear: fadeEnter,
			appearActive: fadeEnterActive,
			enter: fadeEnter,
			enterActive: fadeEnterActive,
			exitActive: fadeExitActive,
		};
		const heroImageUrl = brandingPackage && brandingPackage.HeroImageUrl;
		const givingTitle = brandingPackage && brandingPackage.GivingTitle;
		const backdropImageUrl = brandingPackage && brandingPackage.BackdropImageUrl;
		const backdropMobileImageUrl = brandingPackage && (brandingPackage.BackdropMobileImageUrl || brandingPackage.BackdropImageUrl);
		const showBackdrop =Boolean(backdropImageUrl && backdropMobileImageUrl);
		const transparentBackground = showBackdrop ? scrollStateStore.isAtTopOfThePage : !!heroImageUrl && pageIsGive && scrollStateStore.isAtTopOfThePage;

		const isBabbage = window.location.search.indexOf('babbage=true') > -1;

		const showPayerCoverFeesForApplePay = Boolean(
			currentPageViewModel.type === WebGivingPage.PaymentMethod &&
				merchant.SupportsPayerCoversFees &&
				currentPageViewModel.paymentMethodType === PaymentMethodType.ApplePay
		);

		return (
			<ShouldTranslateProvider hasEntitlement={hasTranslationEnabled}>
				<UiConfigurationContextProvider config={uiConfiguration}>
					<Provider {...getInjectablePropsForProvider(raiseAction)}>
						<div className={wrapper}>
							<ConfettiAnimation
								className={confettiWrapper}
								type={animationType}
								onComplete={hideAnimation}
							/>
							<BrandingTheme theme={theme} />
							{!showBackdrop && heroImageUrl && (
								<ErrorBoundary onError={reportError}>
									<CSSTransition
										in={pageIsGive}
										classNames={fadeTransitionClassNames}
										timeout={parseInt(transitionDurationLongMs, 10)}
										appear
										unmountOnExit
									>
										<HeroImage supportsBackdrop={true} heroImageUrl={heroImageUrl} className={extendedHero} />
									</CSSTransition>
								</ErrorBoundary>
							)}
							{backdropImageUrl && backdropMobileImageUrl && (
								<ErrorBoundary onError={reportError}>
									<CSSTransition
										in={true}
										classNames={fadeTransitionClassNames}
										timeout={parseInt(transitionDurationLongMs, 10)}
										appear
										unmountOnExit
									>
										<Backdrop imageUrl={backdropImageUrl} mobileUrl={backdropMobileImageUrl} />
									</CSSTransition>
								</ErrorBoundary>
							)}
							<ErrorBoundary onError={reportError}>
								<WebGivingMainHeader
									person={person}
									brandingPackage={brandingPackage}
									merchant={merchant}
									setIsAccountDetailsOpen={setIsAccountDetailsOpen}
									isAccountVisible={isAccountDetailsOpen}
									manageAccountUrl={generateManageAccountUrl(webGivingPushpayInfo, person)}
									setAppLoading={setAppLoading}
									hasTranslationEnabled={hasTranslationEnabled}
									unavailableLanguages={unavailableLanguages}
									failedToLoadLanguage={failedToLoadLanguage}
									onError={reportError}
									transparentBackground={transparentBackground}
									hideAccount={uiConfiguration.HideAccount}
									hideI18n={uiConfiguration.HideI18n}
									hasBackdrop={showBackdrop}
									supportsBackdrop={true}
									className={classNames(header, {
										[eclipsed]: isAccountDetailsOpen,
										[loggedIn]: !!person,
									})}
								/>
							</ErrorBoundary>
							<main
								className={classNames(main, {
									[eclipsed]: isAccountDetailsOpen,
									[loggedIn]: !!person,
									[mainFooterSpace]: true,
									[`${loading} brand-primary-before-color`]: isAppLoading,
								})}
							>
								<ErrorBoundary onError={reportError}>
									<WebGivingMainBannerExtended
										visible={pageIsGive}
										className={heroImageUrl ? '' : 'brand-secondary-bg'}
									/>
								</ErrorBoundary>
								<ErrorBoundary onError={reportError}>
									<NotificationController viewModel={notification} />
								</ErrorBoundary>
								{!pageIsGive && (
									<PageTitle
										className={classNames(showBackdrop && pageTitle)}
										title={givingTitle}
										name={merchant.TradingName || merchant.LegalName}
									></PageTitle>
								)}
								<div className={`container ${container}`}>
									<Panel
										className={classNames(mainPanel, pageIsGive && mainPanelLifted)}
										panelType={pageIsGive ? PanelType.White : PanelType.Highlight}
										responsive
									>
										<Router
											currentPage={currentPageViewModel}
											setCurrentPageShouldBeVisible={setCurrentPageShouldBeVisible}
											onError={reportError}
										/>
									</Panel>
								</div>
								<ErrorBoundary onError={reportError}>
									<WebGivingMainFooter
										hasBackdrop={showBackdrop}
										merchant={merchant}
										className={pageIsGive ? pageGiveFooter : ''}
										pushpayInfo={webGivingPushpayInfo}
										showSupportedPaymentMethods={pageIsGive}
										showPayerCoverFeesLegalText={showPayerCoverFeesLegalText || showPayerCoverFeesForApplePay
										}
									/>
								</ErrorBoundary>
							</main>
							<CSSTransition
								in={isAccountDetailsOpen}
								classNames={overlayClassNames}
								timeout={parseInt(transitionDurationMs, 10)}
								appear
								unmountOnExit
							>
								<div className={`${overlay} visible-xs`}></div>
							</CSSTransition>
							<ErrorBoundary onError={reportError}>
								<ModalCommodore vm={this.props.vm} appState={appState} onError={reportError} />
							</ErrorBoundary>
							<ErrorBoundary onError={reportError}>
								{NewFeatures.TesterCommandsEnabled && !isBabbage && (
									<React.Suspense fallback={null}>
										<EvidentlyOverridesWidget />
									</React.Suspense>
								)}
							</ErrorBoundary>
						</div>
					</Provider>
				</UiConfigurationContextProvider>
			</ShouldTranslateProvider>
		);
	}
}
